import { useEffect, useRef } from 'react';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import db from '@services/firebase-service';

// Wake-from-sleep resilience for the PowerPoint content add-in.
//
// WebKit/Office WebViews freeze the network stack during slide transitions and when
// the laptop sleeps. A frozen socket is SILENT: the Firestore WebChannel (or its
// long-poll) wedges half-open with no error, so the SDK's reconnect never fires and
// onSnapshot listeners stay stuck on stale cache until the context is torn down.
// (Same failure class documented in services/firebase-service.js and the RTDB-side
// sharedWorkerTransport.ts.)
//
// experimentalAutoDetectLongPolling helps the SDK *choose* a survivable transport,
// but it can't unstick a socket that was frozen mid-flight. So, exactly on the events
// that mean the add-in just woke — visibilitychange→visible, online, pageshow — we
// power-cycle the Firestore network: disableNetwork() forcibly abandons the orphaned
// WebChannels/polls and enableNetwork() reopens fresh ones and replays listeners,
// instead of waiting out a timeout that may never arrive.
//
// This is event-driven by design (matching sharedWorkerTransport's onWake probe) — no
// polling watchdog. A "no snapshot in N seconds" timer can't be used here because
// onSnapshot only fires on change: an idle-but-healthy doc looks identical to a stall.
//
// Scope: PowerPoint only. Cycling the network drops and refetches EVERY Firestore
// listener in the app, so mount this solely in the add-in context (via useOffice's
// isPowerpoint gate in _app.js), never globally.
export default function usePowerPointFirestoreResilience(): void {
  // Coalesces the burst of wake events (focus + visibilitychange + online often fire
  // together) into a single in-flight cycle; overlapping disable/enable calls interleave.
  const cyclingRef = useRef(false);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const cycle = async (reason: string) => {
      if (cyclingRef.current) return;
      cyclingRef.current = true;
      try {
        await disableNetwork(db);
        await enableNetwork(db);
        console.log(`[firestore-resilience] network cycled after ${reason}`);
      } catch (e) {
        // If disableNetwork rejected we may still be online (fine); if enableNetwork
        // rejected we'd be stuck offline, so make a best-effort re-enable before giving
        // up — leaving the SDK offline is the one outcome we must avoid.
        try {
          await enableNetwork(db);
        } catch {
          // nothing more we can do; next wake event will retry.
        }
        console.error('[firestore-resilience] cycle failed:', e);
      } finally {
        cyclingRef.current = false;
      }
    };

    // Only act when the add-in is actually foregrounded — cycling for a hidden tab
    // just churns; the next visibility gain will handle it. (Mirrors onWake in
    // realtimeSync/sharedWorkerTransport.ts.)
    const onWake = (reason: string) => () => {
      if (document.visibilityState === 'visible') void cycle(reason);
    };

    const onVisibility = onWake('visibilitychange');
    const onOnline = onWake('online');
    const onPageShow = onWake('pageshow');

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
